import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  ShieldCheck, AlertCircle, CircleDashed, Loader2, ArrowRight, Wallet,
  Lock, Banknote, ExternalLink, Anchor, CheckCircle2,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import ComplianceHandoffGateway from "../components/ComplianceHandoffGateway";
import { useAuth } from "../context/AuthContext";

/**
 * /funding — the member-facing partner funding portal.
 *
 * Three-step stepper:
 *   1. Arms-Length Compliance Handoff signed   (handoff_signed)
 *   2. Partner Account Provisioned by ops      (paytiko_customer_provisioned)
 *   3. Funding Request Submitted               (latest_request)
 *
 * The compliance gate is the linchpin: the user CANNOT submit a funding
 * request without first completing the ComplianceHandoffGateway. The same
 * gate is enforced server-side too.
 */
const FundingPage = () => {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gatewayOpen, setGatewayOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Admin-only state: pending funding-request queue + the mark-funded dialog.
  const [adminQueue, setAdminQueue] = useState([]);
  const [adminQueueLoading, setAdminQueueLoading] = useState(false);
  // STARSEED-CAPMARKETS-064: pending FinBPM wire-detail changes awaiting
  // second-admin approval.
  const [wireChanges, setWireChanges] = useState([]);
  const [markFundedTarget, setMarkFundedTarget] = useState(null);
  const [markFundedReference, setMarkFundedReference] = useState("");
  const [markFundedNotes, setMarkFundedNotes] = useState("");
  const [markFundedSubmitting, setMarkFundedSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/paytiko/me/status");
      setStatus(r.data || {});
    } catch (err) {
      console.error("FundingPage: failed to load status", err);
      toast.error("Failed to load funding status.");
    } finally {
      setLoading(false);
    }
  };

  const refreshAdminQueue = useCallback(async () => {
    if (!isAdmin) return;
    setAdminQueueLoading(true);
    try {
      const r = await api.get("/admin/paytiko/funding-requests", {
        params: { status_filter: "requested" },
      });
      setAdminQueue(r.data?.requests || []);
    } catch (err) {
      console.error("FundingPage: failed to load admin queue", err);
      toast.error("Failed to load admin funding queue.");
    } finally {
      setAdminQueueLoading(false);
    }
    try {
      const w = await api.get("/settings/wire-detail-changes");
      setWireChanges((w.data?.changes || []).filter((c) => c.status === "pending"));
    } catch (err) {
      // Non-fatal — the approvals card simply doesn't render.
      console.error("FundingPage: failed to load wire-detail changes", err);
    }
  }, [isAdmin]);

  const decideWireChange = async (id, action) => {
    try {
      await api.post(`/settings/wire-detail-changes/${id}/${action}`);
      toast.success(action === "approve"
        ? "Wire-detail change approved — members now see the new details."
        : "Wire-detail change rejected.");
      refreshAdminQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action} the change`);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshAdminQueue();
  }, [refreshAdminQueue]);

  const openMarkFunded = (req) => {
    setMarkFundedTarget(req);
    setMarkFundedReference("");
    setMarkFundedNotes("");
  };

  const closeMarkFunded = () => {
    setMarkFundedTarget(null);
    setMarkFundedReference("");
    setMarkFundedNotes("");
  };

  const submitMarkFunded = async () => {
    if (!markFundedTarget) return;
    setMarkFundedSubmitting(true);
    try {
      await api.post(
        `/admin/paytiko/funding-requests/${markFundedTarget.id}/mark-funded`,
        {
          reference: markFundedReference.trim() || undefined,
          notes:     markFundedNotes.trim() || undefined,
        },
      );
      toast.success("Marked as funded");
      closeMarkFunded();
      await refreshAdminQueue();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to mark as funded.";
      toast.error(typeof msg === "string" ? msg : "Failed to mark as funded.");
    } finally {
      setMarkFundedSubmitting(false);
    }
  };

  const handoffSigned = !!status?.handoff_signed;
  const provisioned = !!status?.paytiko_customer_provisioned;
  const paytikoLive = !!status?.paytiko_configured;
  const latestReq = status?.latest_request;

  const submitRequest = async () => {
    if (!handoffSigned) {
      setGatewayOpen(true);
      return;
    }
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      toast.error("Enter a funding amount greater than 0.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/paytiko/me/request-funding", {
        amount: num,
        currency: "USD",
        notes: notes.trim() || undefined,
      });
      toast.success("Funding request received. Your account manager will be in touch.");
      setAmount("");
      setNotes("");
      await refresh();
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not submit funding request.";
      toast.error(typeof msg === "string" ? msg : "Could not submit funding request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout
      title="Funding · Partner Platform"
      subtitle="Initiate the arms-length funding handshake from the EchoSense introducer portal to the Partner Platform."
    >
      {loading ? (
        <div className="flex items-center text-slate-500 py-10" data-testid="funding-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading funding status…
        </div>
      ) : (
        <div className="space-y-6" data-testid="funding-page">
          {/* Admin-only — pending FinBPM wire-detail changes (dual-control,
              STARSEED-CAPMARKETS-064). Renders only while a change awaits a
              second admin. */}
          {isAdmin && wireChanges.length > 0 && (
            <Card className="border-amber-500/40 bg-amber-500/[0.05]" data-testid="wire-change-approvals-card">
              <CardHeader>
                <CardTitle className="text-platinum font-serif text-lg flex items-center gap-2">
                  <AlertCircle className="text-amber-300" size={18} />
                  Admin — Wire-Detail Change Awaiting Approval
                </CardTitle>
                <CardDescription className="text-platinum/60">
                  The FinBPM SSB wire details members wire to cannot change without a second
                  admin's approval. Verify the new values with FinBPM out-of-band before
                  approving.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {wireChanges.map((c) => (
                  <div key={c.id} className="rounded-md border border-amber-500/30 p-4 space-y-2" data-testid={`wire-change-${c.id}`}>
                    <p className="text-xs text-platinum/60">
                      Proposed by {c.proposed_by} · {new Date(c.proposed_at).toLocaleString()}
                    </p>
                    {Object.entries(c.fields || {}).map(([field, v]) => (
                      <p key={field} className="text-sm text-platinum/85 font-mono">
                        {field.replace("finbpm_wire_", "")}: {v.old || "—"} → <span className="text-amber-200">{v.new || "—"}</span>
                      </p>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" className="bg-amber-400 hover:bg-amber-300 text-slate-900"
                        onClick={() => decideWireChange(c.id, "approve")} data-testid="wire-change-approve">
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" className="border-platinum/30 text-platinum"
                        onClick={() => decideWireChange(c.id, "reject")} data-testid="wire-change-reject">
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Admin-only — funding queue at top. Visually distinct from the
              member 3-step stepper. Only renders for admin/superuser roles
              (server-side enforces 403 too — defense in depth). */}
          {isAdmin && (
            <Card
              className="border-sky-500/30 bg-sky-500/[0.04]"
              data-testid="funding-admin-queue-card"
            >
              <CardHeader>
                <CardTitle className="text-platinum font-serif text-lg flex items-center gap-2">
                  <ShieldCheck className="text-sky-300" size={18} />
                  Admin — Funding Queue
                </CardTitle>
                <CardDescription className="text-platinum/60">
                  Pending member funding requests. Once you have received the wire
                  out-of-band, click <span className="text-sky-300">Mark as Funded</span> to flip
                  the request status.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {adminQueueLoading ? (
                  <div className="flex items-center text-slate-400 text-sm py-3">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading queue…
                  </div>
                ) : adminQueue.length === 0 ? (
                  <p className="text-slate-500 text-sm py-2" data-testid="funding-admin-queue-empty">
                    No pending funding requests.
                  </p>
                ) : (
                  <div className="overflow-x-auto" data-testid="funding-admin-queue-table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-platinum/10">
                          <th className="text-left py-2 pr-3">Member</th>
                          <th className="text-left py-2 pr-3">Amount</th>
                          <th className="text-left py-2 pr-3">Requested</th>
                          <th className="text-right py-2 pl-3">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminQueue.map((req) => (
                          <tr
                            key={req.id}
                            className="border-b border-platinum/5 last:border-0"
                            data-testid={`funding-admin-queue-row-${req.id}`}
                          >
                            <td className="py-2.5 pr-3 text-slate-200">
                              <div className="font-medium">{req.user_full_name || "—"}</div>
                              <div className="text-xs text-slate-500">{req.user_email || "—"}</div>
                            </td>
                            <td className="py-2.5 pr-3 text-slate-100">
                              ${Number(req.amount).toLocaleString()} {req.currency}
                            </td>
                            <td className="py-2.5 pr-3 text-slate-400 text-xs">
                              {req.requested_at
                                ? new Date(req.requested_at).toLocaleString()
                                : "—"}
                            </td>
                            <td className="py-2.5 pl-3 text-right">
                              <Button
                                size="sm"
                                onClick={() => openMarkFunded(req)}
                                className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold"
                                data-testid={`funding-admin-mark-funded-btn-${req.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                Mark as Funded
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Top notice — explicit introducer-only positioning */}
          <Card className="border-amber-400/30 bg-amber-400/[0.04]">
            <CardContent className="p-4 flex items-start gap-3 text-sm">
              <Anchor className="text-amber-400 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-slate-300 leading-relaxed">
                <span className="text-amber-300 font-semibold">Funds never flow through EchoSense Capital Club.</span>{" "}
                All wire transfers are processed through the regulated payments rail into your
                Partner Account. EchoSense remains an introducer-only platform and
                receives no profit participation from any trading program surfaced past this
                gateway.
              </div>
            </CardContent>
          </Card>

          {/* 3-step stepper */}
          <div className="grid md:grid-cols-3 gap-4" data-testid="funding-stepper">
            <StepCard
              number="1"
              title="Arms-Length Handoff"
              icon={ShieldCheck}
              status={handoffSigned ? "done" : "active"}
              detail={handoffSigned
                ? `Signed ${new Date(status.handoff_signed_at).toLocaleDateString()}`
                : "Required before any funding request. Records your acknowledgment of the introducer-only relationship."}
              badge={handoffSigned ? "SIGNED" : "Action required"}
              cta={handoffSigned ? null : (
                <Button
                  onClick={() => setGatewayOpen(true)}
                  className="w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold"
                  data-testid="funding-sign-handoff-btn"
                >
                  Sign Compliance Handoff
                </Button>
              )}
            />
            <StepCard
              number="2"
              title="Partner Account"
              icon={Wallet}
              status={
                !handoffSigned ? "locked" :
                provisioned ? "done" : "pending"
              }
              detail={
                !handoffSigned
                  ? "Sign the compliance handoff to unlock this step."
                  : provisioned
                    ? "Your Partner Account is provisioned and ready."
                    : "The partner will provision your Partner Account within 24 business hours."
              }
              badge={
                !handoffSigned ? null :
                provisioned ? "PROVISIONED" : "Pending provision"
              }
            />
            <StepCard
              number="3"
              title="Funding Request"
              icon={Banknote}
              status={
                !handoffSigned ? "locked" :
                latestReq ? "done" : "pending"
              }
              detail={
                !handoffSigned
                  ? "Sign the compliance handoff first."
                  : latestReq
                    ? `Last request: $${latestReq.amount.toLocaleString()} ${latestReq.currency} (${latestReq.status})`
                    : "Submit your funding intent below. Your account manager will follow up with wire instructions."
              }
              badge={
                !handoffSigned ? null :
                latestReq ? latestReq.status?.toUpperCase() : "Awaiting submission"
              }
            />
          </div>

          {/* Live-keys warning when Paytiko hasn't been configured server-side */}
          {!paytikoLive && (
            <Card
              className="border-orange-400/30 bg-orange-400/[0.05]"
              data-testid="paytiko-pending-banner"
            >
              <CardContent className="p-4 flex items-start gap-3 text-sm text-slate-300">
                <AlertCircle className="text-orange-300 flex-shrink-0 mt-0.5" size={18} />
                <div className="leading-relaxed">
                  <span className="text-orange-200 font-semibold">Partner live activation pending.</span>{" "}
                  We are awaiting production API keys for the partner integration and the
                  underlying payments rail. You may sign the compliance handoff and submit
                  your funding intent now — your account manager will initiate the actual
                  wire transfer the moment the gateway goes live.
                </div>
              </CardContent>
            </Card>
          )}

          {/* Funding request form */}
          <Card className="border-platinum/10 bg-navy-light/30">
            <CardHeader>
              <CardTitle className="text-platinum font-serif text-xl">Submit Funding Intent</CardTitle>
              <CardDescription className="text-platinum/60">
                Tell us how much you would like to fund your partner account. This does
                not move money — it triggers your account manager to confirm details with you
                and prepare the wire instructions through the payments rail.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-platinum/80 text-sm">Intended Funding Amount (USD)</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 100000"
                  className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                  data-testid="funding-amount-input"
                  min="0"
                  step="1000"
                />
              </div>
              <div>
                <Label className="text-platinum/80 text-sm">Notes for your account manager (optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Preferred timing, source-of-funds bank, etc."
                  className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                  data-testid="funding-notes-input"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={submitRequest}
                  disabled={submitting}
                  className="flex-1 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold disabled:opacity-40"
                  data-testid="funding-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                  ) : handoffSigned ? (
                    <>Submit Funding Request <ArrowRight size={14} className="ml-2" /></>
                  ) : (
                    <><Lock className="w-4 h-4 mr-2" /> Sign Handoff & Continue</>
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                By submitting, you confirm you will follow wire instructions issued exclusively by
                the partner or the regulated payments rail.
                EchoSense never receives client funds.
              </p>
            </CardContent>
          </Card>

          {/* Recent request status (if any) */}
          {latestReq && (
            <Card className="border-platinum/10 bg-navy-light/30" data-testid="funding-latest-card">
              <CardHeader>
                <CardTitle className="text-platinum text-base font-serif">Latest Funding Request</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-300 space-y-1.5">
                <Row label="Amount" value={`$${latestReq.amount.toLocaleString()} ${latestReq.currency}`} />
                <Row label="Status" value={
                  <Badge className="bg-amber-400/20 text-amber-200 border border-amber-400/40 capitalize">
                    {latestReq.status}
                  </Badge>
                } />
                <Row label="Submitted" value={new Date(latestReq.requested_at).toLocaleString()} />
                {latestReq.notes && <Row label="Notes" value={latestReq.notes} />}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Admin-only mark-funded dialog */}
      <Dialog
        open={!!markFundedTarget}
        onOpenChange={(o) => { if (!o) closeMarkFunded(); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="funding-mark-funded-dialog">
          <DialogHeader>
            <DialogTitle>Mark as Funded</DialogTitle>
            <DialogDescription>
              {markFundedTarget && (
                <>
                  Confirm the wire was received for{" "}
                  <span className="text-slate-200 font-semibold">
                    {markFundedTarget.user_full_name || markFundedTarget.user_email}
                  </span>{" "}
                  (${Number(markFundedTarget.amount).toLocaleString()} {markFundedTarget.currency}).
                  Both fields below are optional.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Wire reference (optional)</Label>
              <Input
                value={markFundedReference}
                onChange={(e) => setMarkFundedReference(e.target.value)}
                placeholder="e.g. WIRE-12345"
                className="mt-1.5"
                maxLength={200}
                data-testid="funding-mark-funded-reference-input"
              />
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Input
                value={markFundedNotes}
                onChange={(e) => setMarkFundedNotes(e.target.value)}
                placeholder="e.g. Received via Chase on 5/29"
                className="mt-1.5"
                maxLength={1000}
                data-testid="funding-mark-funded-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeMarkFunded}
              disabled={markFundedSubmitting}
              data-testid="funding-mark-funded-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitMarkFunded}
              disabled={markFundedSubmitting}
              className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold"
              data-testid="funding-mark-funded-confirm-btn"
            >
              {markFundedSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Marking…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirm</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The arms-length compliance gate — same component used in Deal Room */}
      <ComplianceHandoffGateway
        open={gatewayOpen}
        onClose={() => setGatewayOpen(false)}
        onProceed={async () => {
          setGatewayOpen(false);
          await refresh();
          toast.success("Handoff recorded — you may now submit your funding intent.");
        }}
        gateway="finbpm_monaxa"
        context="Partner Platform · regulated payments rail"
      />
    </DashboardLayout>
  );
};

const StepCard = ({ number, title, icon: Icon, status, detail, cta, badge }) => {
  const palette = {
    done:    { bg: "bg-emerald-500/[0.08]", border: "border-emerald-500/40", text: "text-emerald-300", badgeBg: "bg-emerald-500/15", badgeText: "text-emerald-300" },
    active:  { bg: "bg-amber-400/[0.07]",   border: "border-amber-400/50",   text: "text-amber-300",   badgeBg: "bg-amber-400/15",  badgeText: "text-amber-300" },
    pending: { bg: "bg-slate-500/[0.06]",   border: "border-slate-500/30",   text: "text-slate-400",   badgeBg: "bg-slate-500/15",  badgeText: "text-slate-300" },
    locked:  { bg: "bg-slate-500/[0.03]",   border: "border-slate-700/40",   text: "text-slate-600",   badgeBg: "bg-slate-700/30",  badgeText: "text-slate-500" },
  }[status] || {};

  return (
    <Card
      className={`${palette.bg} ${palette.border} border`}
      data-testid={`funding-step-${number}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full border ${palette.border} ${palette.bg} flex items-center justify-center shrink-0`}>
            {status === "done"
              ? <ShieldCheck className={palette.text} size={16} />
              : status === "locked"
                ? <Lock className={palette.text} size={14} />
                : <Icon className={palette.text} size={16} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className={`text-[10px] uppercase tracking-[0.25em] ${palette.text} font-semibold`}>
                Step {number}
              </p>
              {badge && (
                <span
                  className={`text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${palette.badgeBg} ${palette.badgeText} border ${palette.border}`}
                  data-testid={`funding-step-${number}-badge`}
                >
                  {badge}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-100 font-semibold mt-0.5">{title}</p>
            <p className="text-xs text-slate-400 mt-1 leading-snug">{detail}</p>
            {cta && <div className="mt-3">{cta}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-slate-500 text-xs uppercase tracking-wider">{label}</span>
    <span className="text-slate-100 text-sm">{value}</span>
  </div>
);

export default FundingPage;
